import React, { useState } from 'react'
import i18n from 'simple-react-i18n'
import useBoolean from 'utils/customHook/useBoolean'
import { Button, Dialog, DialogActions, DialogContent, Grid2, Icon, Stack, Tooltip } from '@mui/material'
import { useDispatch } from 'react-redux'
import OperationAction from 'quality/components/operation/actions/OperationAction'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import { isNil, isUndefined, keys, omitBy, values } from 'lodash'
import PropTypes from 'prop-types'
import Checkbox from 'components/forms/Checkbox'
import MessageCard from 'components/card/MessageCard'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'

const CalculateIndexes = ({
    qualitometer,
    operations = [],
    support,
}) => {
    const dispatch = useDispatch()

    const {
        value: isOpen,
        setTrue: open,
        setFalse: close,
    } = useBoolean(false)
    const [options, setOptions] = useState({})

    const onCalculate = () => {
        if (operations.length === 0 || isNil(qualitometer)) return
        dispatch(OperationAction.calculateSEEE(qualitometer, operations, keys(omitBy(options, o => !o))))
    }

    return (
        <div className='action-wrapper'>
            <Tooltip title={i18n.calculSeeeDesc} placement='bottom'>
                <Icon
                    sx={{ fontSize: '3rem', width: '69px', height: '64px', paddingTop: '12px', cursor: 'pointer' }}
                    onClick={() => {
                        open()
                        setOptions({})
                    }}
                >sync</Icon>
            </Tooltip>
            <Dialog
                maxWidth='md'
                fullWidth
                open={isOpen}
                slotProps={{
                    paper: {
                        sx: {
                            minHeight: undefined,
                            maxHeight: undefined,
                        },
                    },
                }}
            >
                <DefaultDialogTitle title={i18n.calculSeeeDesc} onClose={close} />
                <DialogContent>
                    <Stack spacing={1}>
                        <StyledFieldSet className='width-100 margin-right-10' >
                            <Grid2 size={3}>
                                <Checkbox
                                    label={ i18n.noCalculIfExist } checked={options.noCalculIfExist}
                                    onChange={v => setOptions(p => ({ ...p, noCalculIfExist: v }))}
                                    data-cy='noCalculIfExist'
                                />
                            </Grid2>
                            <Grid2 size={3}>
                                <SimpleDatePicker
                                    label={ i18n.startDate }
                                    checked={options.beginDate}
                                    onChange={v => setOptions(p => ({ ...p, beginDate: v }))}
                                    data-cy='startDate'
                                />
                            </Grid2>
                            <Grid2 size={3} >
                                <SimpleDatePicker
                                    label={ i18n.endDate }
                                    checked={options.endDate}
                                    onChange={v => setOptions(p => ({ ...p, endDate: v }))}
                                    data-cy='endDate'
                                />
                            </Grid2>
                        </StyledFieldSet>
                        {(isUndefined(support) || support === 13) && (
                            <StyledFieldSet style={{ marginTop: '0px' }}>
                                <StyledLegend>{i18n.macroInvertebres}</StyledLegend>
                                <Grid2 container>
                                    <Grid2 size={3}>
                                        <Checkbox
                                            label={i18n.ibgdce}
                                            checked={options.ibgdce}
                                            onChange={v => setOptions(p => ({ ...p, ibgdce: v }))}
                                        />
                                    </Grid2>
                                    <Grid2 size={3}>
                                        <Checkbox
                                            label={i18n.mgce}
                                            checked={options.mgce}
                                            onChange={v => setOptions(p => ({ ...p, mgce: v }))}
                                        />
                                    </Grid2>
                                    <Grid2 size={3}>
                                        <Checkbox
                                            label={i18n.i2m2}
                                            checked={options.i2m2}
                                            onChange={v => setOptions(p => ({ ...p, i2m2: v }))}
                                        />
                                    </Grid2>
                                </Grid2>
                            </StyledFieldSet>
                        )}
                        {(isUndefined(support) || support === 4) && (
                            <StyledFieldSet style={{ marginTop: '0px' }}>
                                <StyledLegend>{i18n.fish}</StyledLegend>
                                <Grid2 container>
                                    <Grid2 size={3}>
                                        <Checkbox
                                            label={i18n.ipr}
                                            checked={options.ipr}
                                            onChange={v => setOptions(p => ({ ...p, ipr: v }))}
                                            disabled
                                        />
                                    </Grid2>
                                    <Grid2 size={3}>
                                        <Checkbox
                                            label={i18n.iprPlus}
                                            checked={options.iprPlus}
                                            onChange={v => setOptions(p => ({ ...p, iprPlus: v }))}
                                            disabled
                                        />
                                    </Grid2>
                                    <Grid2 size={3}>
                                        <Checkbox
                                            label={i18n.iil}
                                            checked={options.iil}
                                            onChange={v => setOptions(p => ({ ...p, iil: v }))}
                                            disabled
                                        />
                                    </Grid2>
                                </Grid2>
                            </StyledFieldSet>
                        )}
                        {(isUndefined(support) || support === 13) && (
                            <StyledFieldSet style={{ marginTop: '0px' }}>
                                <StyledLegend>{i18n.diagnostics}</StyledLegend>
                                <Grid2 container>
                                    <Grid2 size={3}>
                                        <Checkbox
                                            checked={options.ODInvertebres}
                                            label={i18n.ODInvertebres}
                                            onChange={v => setOptions(p => ({ ...p, ODInvertebres: v }))}
                                            data-cy='ODInvertebres'
                                        />

                                    </Grid2>
                                    <Grid2 size={3}>
                                        <Checkbox
                                            checked={options.iprPlus}
                                            label={i18n.iprPlus}
                                            onChange={v => setOptions(p => ({ ...p, iprPlus: v }))}
                                            disabled
                                            data-cy='iprPlus'
                                        />

                                    </Grid2>
                                    <Grid2 size={3}>
                                        <Checkbox
                                            checked={options.idmTrait}
                                            label={i18n.idmTrait}
                                            onChange={v => setOptions(p => ({ ...p, idmTrait: v }))}
                                            disabled
                                            data-cy='idmTrait'
                                        />

                                    </Grid2>
                                </Grid2>
                            </StyledFieldSet>
                        )}
                        {(isUndefined(support) || support === 10 || support === 27) && (
                            <StyledFieldSet style={{ marginTop: '0px' }}>
                                <StyledLegend>{i18n.diatomees} et {i18n.macrophyte}</StyledLegend>
                                <Grid2 container>
                                    <Grid2 size={3}>
                                        <Checkbox
                                            label={i18n.ibd}
                                            checked={options.ibd}
                                            onChange={v => setOptions(p => ({ ...p, ibd: v }))}
                                        />
                                    </Grid2>
                                    <Grid2 size={3}>
                                        <Checkbox
                                            label={i18n.ibmr}
                                            checked={options.ibmr}
                                            onChange={v => setOptions(p => ({ ...p, ibmr: v }))}
                                            disabled
                                        />
                                    </Grid2>
                                </Grid2>
                            </StyledFieldSet>
                        )}
                        {(isUndefined(support) || support === 11) && (
                            <StyledFieldSet style={{ marginTop: '0px' }}>
                                <StyledLegend>{i18n.phytoplanctons }</StyledLegend>
                                <Grid2 container>
                                    <Grid2 size={3}>
                                        <Checkbox
                                            label={i18n.iplac}
                                            checked={options.iplac}
                                            onChange={v => setOptions(p => ({ ...p, iplac: v }))}
                                            disabled
                                        />
                                    </Grid2>
                                    <Grid2 size={3}>
                                        <Checkbox
                                            label={i18n.iphyge}
                                            checked={options.iphyge}
                                            onChange={v => setOptions(p => ({ ...p, iphyge: v }))}
                                            disabled
                                        />
                                    </Grid2>
                                </Grid2>
                            </StyledFieldSet>
                        )}
                        {![4, 10, 11, 13, 27, undefined].includes(support) && (
                            <MessageCard>{i18n.noCalculAvailable}</MessageCard>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={onCalculate}
                        variant='contained'
                        color='primary'
                        disabled={!values(options).some(v => v)}
                    >
                        {i18n.calculate}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

CalculateIndexes.propTypes = {
    qualitometer: PropTypes.number,
    operations: PropTypes.arrayOf(PropTypes.number),
    support: PropTypes.string,
}

export default CalculateIndexes